import { NavLink } from 'react-router-dom'

import { useSplitEnabled } from '@buffer-mono/features'
import { useReferralProgramNavigation } from '~publish/hooks/useReferralProgramNavigation'
import {
  AllChannelsIcon,
  ArrowDownUpIcon,
  UsersIcon,
  BellIcon,
  Building2Icon,
  ClipboardListIcon,
  CodeXmlIcon,
  CircleDollarSignIcon,
  Flex,
  FlaskConicalIcon,
  FolderIcon,
  GripIcon,
  HandshakeIcon,
  LayoutGridIcon,
  MessageCircleHeartIcon,
  GiftIcon,
  NewBadge,
  Sidebar,
  SlidersVerticalIcon,
  TagIcon,
  Text,
  UpgradeBadge,
  UserRoundCheckIcon,
  UserIcon,
} from '@buffer-mono/popcorn'

import {
  useCurrentOrganization,
  useHasOrganizationAction,
  useIsFreeUser,
  useIsOneBufferOrg,
} from '~publish/contexts/AccountContext'
import { useIsViewOnlyPublishUser } from '~publish/hooks/useIsViewOnlyPublishUser'
import {
  betaFeaturesRoute,
  generalSettingsRoute,
  referAFriendRoute,
  settingsBillingRoute,
  settingsChannelGroupsRoute,
  settingsChannelsRoute,
  settingsImportRoute,
  settingsRoleAndPermissionsRoute,
  settingsSavedRepliesRoute,
  settingsTagsRoute,
} from '~publish/legacy/routes'

import useConnectedChannelsCount from './Channels/hooks/useConnectedChannelsCount'
import { usePersonalAccessTokens } from './DeveloperDashboard/hooks'
import { ExpiredTokenIndicator } from './shared/components/ExpiredTokenIndicator'
import { useCanAccessTeamFeatures } from './Team/hooks/useCanAccessTeamFeatures'

import styles from './SettingsNavigationContent.module.css'

import type { JSX } from 'react'

const affiliateProgramRoute = {
  route: '/settings/partner-with-buffer',
}

type SettingsNavigationContentProps = {
  onNavigate?: () => void
}

export function SettingsNavigationContent({
  onNavigate,
}: SettingsNavigationContentProps): JSX.Element {
  const currentOrganization = useCurrentOrganization()
  const isOneBufferOrganization = useIsOneBufferOrg()
  const showBetaFeatures = Boolean(
    !currentOrganization || isOneBufferOrganization,
  )

  const { isEnabled: affiliateProgramEnabled, isReady: affiliateProgramReady } =
    useSplitEnabled('dub-affiliate-program')

  const referralProgram = useSplitEnabled('geid-referral-program')
  const referralNavigation = useReferralProgramNavigation({
    isReady: referralProgram.isReady,
    isEnabled: referralProgram.isEnabled,
    affiliateProgramEnabled,
  })
  const showReferralProgram = referralNavigation !== 'legacy'

  const { isEnabled: migrationAssistantEnabled } = useSplitEnabled(
    'migration-assistant',
  )

  const isFreeUser = useIsFreeUser()
  const channelCountBadge = useConnectedChannelsCount()
  const { canAccessTeamFeatures } = useCanAccessTeamFeatures()
  const { hasExpiredToken } = usePersonalAccessTokens()

  const canEditOrganization = useHasOrganizationAction('edit')
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const canManageBilling = useHasOrganizationAction('manageBilling')
  const canManageTags = useHasOrganizationAction('manageTags')
  const canManageSavedReplies = useHasOrganizationAction('manageSavedReplies')
  const canManageIntegrations = useHasOrganizationAction('manageIntegrations')
  const isViewOnlyPublishUser = useIsViewOnlyPublishUser()
  // Read-only view of the current user's own role and channel access, for
  // members who cannot open the Team page, which shows the same ground. Under
  // Roles every member can view the team, so only legacy members and plans
  // without the team panel still need it. The page stays reachable by URL for
  // any role.
  const showRolePermissions =
    Boolean(currentOrganization) && !canAccessTeamFeatures
  // Manage Channels scopes its list and actions to the member's per-channel
  // access, so any member who can see at least one channel gets the entry;
  // admins keep it even with zero connected channels (to connect one).
  const showChannels = canManageChannels || channelCountBadge > 0
  const showOrganizationSection =
    canEditOrganization ||
    canManageChannels ||
    canManageBilling ||
    canAccessTeamFeatures ||
    showRolePermissions ||
    showChannels

  return (
    <>
      {/* Account Section */}
      <Sidebar.Group>
        <Sidebar.GroupLabel>Account</Sidebar.GroupLabel>
        <Sidebar.GroupContent>
          <Sidebar.List>
            <Sidebar.ListItem>
              <Sidebar.Button
                prefix={<UserIcon />}
                as={NavLink}
                end
                to="/settings"
                onClick={onNavigate}
              >
                Profile
              </Sidebar.Button>
            </Sidebar.ListItem>
            <Sidebar.ListItem>
              <Sidebar.Button
                prefix={<SlidersVerticalIcon />}
                as={NavLink}
                to="/settings/preferences"
                onClick={onNavigate}
              >
                Preferences
              </Sidebar.Button>
            </Sidebar.ListItem>
            <Sidebar.ListItem>
              <Sidebar.Button
                prefix={<BellIcon />}
                as={NavLink}
                to="/settings/notifications"
                onClick={onNavigate}
              >
                Notifications
              </Sidebar.Button>
            </Sidebar.ListItem>
          </Sidebar.List>
        </Sidebar.GroupContent>
      </Sidebar.Group>

      {/* Organization Section */}
      {showOrganizationSection && (
        <Sidebar.Group>
          <Sidebar.GroupLabel>Organization</Sidebar.GroupLabel>
          <Sidebar.GroupContent>
            <Sidebar.List>
              {canEditOrganization && (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<Building2Icon />}
                    as={NavLink}
                    to={generalSettingsRoute.route}
                    onClick={onNavigate}
                  >
                    General
                  </Sidebar.Button>
                </Sidebar.ListItem>
              )}
              {showRolePermissions && (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<UserRoundCheckIcon />}
                    as={NavLink}
                    to={settingsRoleAndPermissionsRoute.route}
                    onClick={onNavigate}
                  >
                    Role & Permissions
                  </Sidebar.Button>
                </Sidebar.ListItem>
              )}
              {showChannels && (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<AllChannelsIcon />}
                    as={NavLink}
                    to={settingsChannelsRoute.route}
                    onClick={onNavigate}
                    suffix={
                      <Text
                        weight="medium"
                        className={styles.channelCountBadge}
                      >
                        {channelCountBadge}
                      </Text>
                    }
                  >
                    Channels
                  </Sidebar.Button>
                </Sidebar.ListItem>
              )}
              {canAccessTeamFeatures && (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<UsersIcon />}
                    as={NavLink}
                    to="/settings/team"
                    onClick={onNavigate}
                  >
                    Team Members
                  </Sidebar.Button>
                </Sidebar.ListItem>
              )}
              {canManageBilling && (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<CircleDollarSignIcon />}
                    as={NavLink}
                    to={settingsBillingRoute.route}
                    onClick={onNavigate}
                  >
                    Billing
                  </Sidebar.Button>
                </Sidebar.ListItem>
              )}
            </Sidebar.List>
          </Sidebar.GroupContent>
        </Sidebar.Group>
      )}

      {/* Features Section */}
      <Sidebar.Group>
        <Sidebar.GroupLabel>Features</Sidebar.GroupLabel>
        <Sidebar.GroupContent>
          <Sidebar.List>
            {canManageTags && (
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<TagIcon />}
                  as={NavLink}
                  to={settingsTagsRoute.route}
                  onClick={onNavigate}
                >
                  Tags
                </Sidebar.Button>
              </Sidebar.ListItem>
            )}
            <Sidebar.ListItem>
              <Sidebar.Button
                prefix={<FolderIcon />}
                as={NavLink}
                to={settingsChannelGroupsRoute.route}
                onClick={onNavigate}
                suffix={isFreeUser ? <UpgradeBadge size="small" /> : undefined}
              >
                Channel Groups
              </Sidebar.Button>
            </Sidebar.ListItem>
            {canManageSavedReplies && (
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<ClipboardListIcon />}
                  as={NavLink}
                  to={settingsSavedRepliesRoute.route}
                  onClick={onNavigate}
                >
                  Saved Replies
                </Sidebar.Button>
              </Sidebar.ListItem>
            )}
          </Sidebar.List>
        </Sidebar.GroupContent>
      </Sidebar.Group>

      {/* Other Section */}
      <Sidebar.Group>
        <Sidebar.GroupLabel>Other</Sidebar.GroupLabel>
        <Sidebar.GroupContent>
          <Sidebar.List>
            <Sidebar.ListItem>
              <Sidebar.Button
                prefix={<CodeXmlIcon />}
                as={NavLink}
                to="/settings/api"
                onClick={onNavigate}
                suffix={
                  <Flex align="center" gap="xs">
                    {hasExpiredToken && <ExpiredTokenIndicator />}
                    <NewBadge />
                  </Flex>
                }
              >
                API
              </Sidebar.Button>
            </Sidebar.ListItem>
            {/* Importing creates posts, so a view-only member has nothing
                to import to */}
            {migrationAssistantEnabled && !isViewOnlyPublishUser && (
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<ArrowDownUpIcon />}
                  as={NavLink}
                  to={settingsImportRoute.route}
                  onClick={onNavigate}
                >
                  Import Content
                </Sidebar.Button>
              </Sidebar.ListItem>
            )}
            <Sidebar.ListItem>
              <Sidebar.Button
                prefix={<GripIcon />}
                as={NavLink}
                to="/settings/apps-extras"
                onClick={onNavigate}
              >
                Apps & Extras
              </Sidebar.Button>
            </Sidebar.ListItem>
            {showBetaFeatures && (
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<FlaskConicalIcon />}
                  as={NavLink}
                  to={betaFeaturesRoute.route}
                  onClick={onNavigate}
                >
                  Beta Features
                </Sidebar.Button>
              </Sidebar.ListItem>
            )}
            {(showReferralProgram || affiliateProgramReady) &&
              (affiliateProgramEnabled && !showReferralProgram ? (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<HandshakeIcon />}
                    as={NavLink}
                    to={affiliateProgramRoute.route}
                    onClick={onNavigate}
                  >
                    Partner Program
                  </Sidebar.Button>
                </Sidebar.ListItem>
              ) : (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={
                      showReferralProgram ? (
                        <GiftIcon />
                      ) : (
                        <MessageCircleHeartIcon />
                      )
                    }
                    as={NavLink}
                    to={referAFriendRoute.route}
                    onClick={onNavigate}
                  >
                    Refer a Friend
                  </Sidebar.Button>
                </Sidebar.ListItem>
              ))}
            {canManageIntegrations && (
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<LayoutGridIcon />}
                  as={NavLink}
                  to="/settings/integrations"
                  onClick={onNavigate}
                  suffix={<NewBadge />}
                >
                  Integrations
                </Sidebar.Button>
              </Sidebar.ListItem>
            )}
          </Sidebar.List>
        </Sidebar.GroupContent>
      </Sidebar.Group>
    </>
  )
}
